import { useEffect, useState } from 'react';

import { Button, Divider, Stack, styled, Typography } from '@mui/material';

import { AgencyApi } from '@/api';
import { AppModal, BaseModalProps } from '@/components';
import { useAgencyStore } from '@/store';

const CFSCaseAlreadyExistsModalRoot = styled(AppModal)(({ theme }) => ({
  '& .modal-content': {
    position: 'absolute',
    minWidth: 500,
    padding: 20,
    borderRadius: 8,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 600,
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: 600,
    },
    '& .btn-close': {
      position: 'absolute',
      right: 8,
      top: 8,
      color: theme.palette.error.main,
    },
    '& .separator': {
      marginTop: 20,
      marginBottom: 20,
    },
  },
}));

export type ExistingCaseMode =
  | 'same-agency'
  | 'different-agency'
  | 'no-permission';

interface CFSCaseAlreadyExistsModalProps
  extends Omit<BaseModalProps, 'onClose'> {
  open: boolean;
  onClose: () => void;
  onCreateNew: () => void;
  onViewCase?: () => void;
  caseNumber: string;
  mode: ExistingCaseMode;
  agencyId?: string;
}

export const CFSCaseAlreadyExistsModal = ({
  open,
  onClose,
  onCreateNew,
  onViewCase,
  caseNumber,
  mode,
  agencyId,
}: CFSCaseAlreadyExistsModalProps) => {
  const { agencies } = useAgencyStore();
  const [fetchedAgencyName, setFetchedAgencyName] = useState<string | null>(
    null,
  );

  const agencyFromStore = agencies.find((agency) => agency._id === agencyId);
  const agencyName =
    agencyFromStore?.name ?? fetchedAgencyName ?? agencyId ?? '';

  useEffect(() => {
    if (!agencyId || agencyFromStore) {
      setFetchedAgencyName(null);
      return;
    }
    let cancelled = false;
    AgencyApi.getAgency(agencyId)
      .then((res) => {
        if (!cancelled) setFetchedAgencyName(res.data?.name ?? null);
      })
      .catch(() => {
        if (!cancelled) setFetchedAgencyName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [agencyId, agencyFromStore]);

  return (
    <CFSCaseAlreadyExistsModalRoot
      onClose={onClose}
      open={open}
      title="Case already exists"
    >
      <Stack rowGap={2} divider={<Divider />}>
        <Typography>
          {mode === 'different-agency' && (
            <>
              A case [{caseNumber}] created for agency [{agencyName}] already
              exists for this CFS. <br />
              Do you still want to create another one?
            </>
          )}
          {mode === 'no-permission' && (
            <>
              A case [{caseNumber}] already exists for this CFS. <br />
              You are restricted from viewing the existing case. Do you still
              want to create another one?
            </>
          )}
          {mode === 'same-agency' && (
            <>
              A case [{caseNumber}] already exists for this CFS. <br />
              You can view the existing case or create another one.
            </>
          )}
        </Typography>

        <Stack flexDirection="row">
          {mode === 'different-agency' && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={onCreateNew}
              sx={{ marginLeft: 'auto' }}
            >
              Create new anyway
            </Button>
          )}
          {mode === 'no-permission' && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={onCreateNew}
              sx={{ marginLeft: 'auto' }}
            >
              Create new anyway
            </Button>
          )}
          {mode === 'same-agency' && (
            <>
              <Button
                variant="outlined"
                color="inherit"
                onClick={onCreateNew}
                sx={{ marginLeft: 'auto', marginRight: '10px' }}
              >
                Create new anyway
              </Button>
              {onViewCase && (
                <Button variant="contained" color="error" onClick={onViewCase}>
                  View case
                </Button>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </CFSCaseAlreadyExistsModalRoot>
  );
};
